import {
  proposalEmptyValueText,
  type ProposalChangeDescriptor,
} from "work/proposals/negotiation/proposal-change-description";
import {
  isProposalFeeScheduleAspect,
  isProposalFeeScheduleTeamMemberProperty,
  proposalFeeScheduleAspectLabels,
  proposalFeeScheduleAspectText,
  proposalFeeScheduleAspects,
  proposalFeeScheduleColumnFromConflictKey,
  proposalFeeScheduleColumnLabel,
  proposalFeeScheduleRestrictedTeamMemberText,
  proposalFeeScheduleTeamMemberProperty,
  readProposalFeeScheduleAspectValue,
  readProposalFeeScheduleCategoryValues,
  type ProposalFeeScheduleCategoryValues,
} from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import type {
  ProposalFeeScheduleAspectChange,
  ProposalFeeScheduleCategoryChange,
  ProposalFeeScheduleChangeSummary,
} from "work/proposals/negotiation/proposal-fee-schedule-change-summary";

/**
 * What a reverted change will actually submit.
 *
 * The submit dialog lets a leader keep one change back, and a kept-back change used to read "Will be
 * restored before submission" — a sentence about the mechanism rather than about the proposal. The
 * reader could see what every other row would send and could not see what that one row would send,
 * which is the one question the dialog exists to answer (#5551).
 *
 * A revert restores the value the counterparty is still looking at, and the change line carries it:
 * `ProposalChangeLine.previousValue`, set by the server on the viewing party's own unpublished lines.
 * So this module reads the PREVIOUS side of a descriptor the way the rest of the change vocabulary
 * reads the next one, and renders it as plain text — no redline, because nothing is moving.
 *
 * Every restored value is a real value, so there is one wording for "there is no value to restore" and
 * it is {@link proposalRestoredValueNotIncludedText}. A masked fee-schedule member is the single
 * exception: the server strips who they are, so the restored reading is the fee schedule table's own
 * word for that state and never an identity.
 */

/**
 * How a row with nothing to restore reads.
 *
 * One wording for the three ways a line can arrive with no previous side: an item that is genuinely new
 * (a revert takes it back out), an API deploy older than `previousValue` (which sends none), and a
 * column whose published value the viewer may not see. "Not included" is true of the submission in all
 * three — the row contributes nothing to what is sent — and it says so without claiming to know a value
 * nobody handed over.
 */
export const proposalRestoredValueNotIncludedText = "Not included";

/**
 * The value a revert of this row will submit, as one line of plain text.
 *
 * Never blank. A previous value that exists and reads as nothing — a field that was published empty —
 * reads "[Empty]", the same wording the dialog already gives a change with nothing on its new side, so
 * an empty published value stays distinguishable from a value the dialog could not render.
 */
export function proposalChangeRestoredValueText(descriptor: ProposalChangeDescriptor): string {
  if (descriptor.previousText !== undefined) return descriptor.previousText;
  return descriptor.previousValueExists ? proposalEmptyValueText : proposalRestoredValueNotIncludedText;
}

/**
 * The fee schedule as a revert of the whole group will submit it.
 *
 * The same summary shape the redline draws, with every column `changed: false` and valued from the
 * previous side, so {@link ProposalFeeScheduleChangeRedline} draws the restored schedule through its
 * plain-text path (`AspectUnchangedValue`) rather than as a redline. A category the submission adds has
 * no previous side at all, so a revert takes it out and the row says so; a category the submission
 * removes comes back whole, because its previous value IS the whole category.
 *
 * Valued rather than re-used text: a rate's `previousText` is rendered from the rate's leaf alone, which
 * carries no currency, so it reads "250" where the redline reads "$250.00". The restored columns are
 * read as values and rendered together, so the restored schedule reads like a fee schedule.
 */
export function summarizeProposalFeeScheduleRestoredDescriptors(
  descriptors: readonly ProposalChangeDescriptor[],
): ProposalFeeScheduleChangeSummary {
  const categoriesByItemId = new Map<string, RestoredCategory>();

  for (const descriptor of descriptors) {
    // The descriptor packs the conflict key and the item into one identity; the conflict key names the
    // column, the item is the category.
    const [conflictKey, itemIdFromKey] = descriptor.key.split("|");
    const itemId = itemIdFromKey ?? descriptor.key;
    let category = categoriesByItemId.get(itemId);
    if (!category) {
      category = {
        itemId,
        itemLabel: descriptor.itemLabel,
        currentValues: {},
        restoredValues: {},
        extras: [],
        removedOnRevert: false,
      };
      categoriesByItemId.set(itemId, category);
    }
    category.itemLabel ??= descriptor.itemLabel;
    // A column this submission does not touch is a column a revert leaves alone, so it stands as it is.
    // Later leaves of the same category fill in what earlier ones did not carry, rather than replacing
    // it: each leaf sees only its own column.
    for (const [aspect, value] of Object.entries(readProposalFeeScheduleCategoryValues(descriptor.itemValue))) {
      if (isProposalFeeScheduleAspect(aspect) && !(aspect in category.currentValues)) {
        category.currentValues[aspect] = value;
      }
    }

    const column = proposalFeeScheduleColumnFromConflictKey(conflictKey);
    if (isProposalFeeScheduleTeamMemberProperty(column)) {
      // The member's name already went through the change vocabulary's resolver. When it did not read —
      // which is what a masked member looks like, the property removed server-side — the restored
      // reading is the table's own word for that state. Never an id, and never the person.
      category.teamMemberText = descriptor.previousText ?? proposalFeeScheduleRestrictedTeamMemberText;
      continue;
    }
    if (column) {
      // An absent previous side restores the column to nothing, and a column holding nothing is not
      // listed — the same rule the redline applies to an untouched empty column.
      category.restoredValues[column] = descriptor.previousValueExists
        ? readProposalFeeScheduleAspectValue(descriptor.previousValue, column)
        : undefined;
      continue;
    }
    if (addressesWholeCategory(conflictKey, itemId)) {
      // The category itself arrived or left. Arriving has no previous side: a revert takes it out again.
      // Leaving carries the whole category as its previous value, so a revert brings all of it back.
      if (!descriptor.previousValueExists) {
        category.removedOnRevert = true;
        continue;
      }
      for (const [aspect, value] of Object.entries(readProposalFeeScheduleCategoryValues(descriptor.previousValue))) {
        if (isProposalFeeScheduleAspect(aspect)) category.restoredValues[aspect] = value;
      }
      continue;
    }
    // A column this build cannot name is still a column the revert restores, and silence about it is the
    // worse failure. It reads from its own text, because nothing here knows how to value it.
    const label = proposalFeeScheduleColumnLabel(lastConflictKeySegment(conflictKey));
    if (descriptor.previousText !== undefined && !category.extras.some((extra) => extra.label === label)) {
      category.extras.push({ label, changed: false, nextText: descriptor.previousText });
    }
  }

  return { categories: [...categoriesByItemId.values()].map(renderRestoredCategory) };
}

type RestoredCategory = {
  itemId: string;
  itemLabel?: string;
  /** The columns as they stand now — the ones this submission does not touch. */
  currentValues: ProposalFeeScheduleCategoryValues;
  /** The columns this submission touches, valued from the side a revert restores. */
  restoredValues: ProposalFeeScheduleCategoryValues;
  /** The restored reading of the assigned member, when the submission reassigns the category. */
  teamMemberText?: string;
  /** Columns this build cannot name, restored from their own text. */
  extras: ProposalFeeScheduleAspectChange[];
  /** Whether a revert takes the whole category out: it arrived since the last submission. */
  removedOnRevert: boolean;
};

/**
 * Draws one restored category: its name, then every column that holds a value, all plainly.
 *
 * `action: "updated"` deliberately, for every case. The restored schedule is not a change — it is what
 * the submission will carry — so no category is drawn as an insertion or struck through, whatever this
 * submission was doing to it.
 */
function renderRestoredCategory(category: RestoredCategory): ProposalFeeScheduleCategoryChange {
  const values: ProposalFeeScheduleCategoryValues = { ...category.currentValues, ...category.restoredValues };
  // The heading is the name the revert restores, not the name the submission gave it: a renamed category
  // goes back to what the counterparty is still reading it as.
  const name = restoredCategoryName(values) ?? category.itemLabel;

  if (category.removedOnRevert) {
    return {
      itemId: category.itemId,
      itemLabel: name,
      action: "updated",
      // No column to hang it off: the whole category is what the revert leaves out.
      aspects: [{ label: "", changed: false, nextText: proposalRestoredValueNotIncludedText }],
    };
  }

  const aspects: ProposalFeeScheduleAspectChange[] = [];
  for (const aspect of proposalFeeScheduleAspects) {
    const text = proposalFeeScheduleAspectText(aspect, values[aspect], values);
    if (text === undefined) continue;
    // The row is headed by the name, so listing it again would print it twice.
    if (aspect === "name" && text === name) continue;
    aspects.push({ aspect, label: proposalFeeScheduleAspectLabels[aspect], changed: false, nextText: text });
  }
  if (category.teamMemberText !== undefined) {
    aspects.push({
      label: proposalFeeScheduleColumnLabel(proposalFeeScheduleTeamMemberProperty),
      changed: false,
      nextText: category.teamMemberText,
    });
  }

  return {
    itemId: category.itemId,
    itemLabel: name,
    action: "updated",
    aspects: [...aspects, ...category.extras],
  };
}

function restoredCategoryName(values: ProposalFeeScheduleCategoryValues): string | undefined {
  const name = values.name;
  return typeof name === "string" && name.trim() ? name.trim() : undefined;
}

/**
 * Whether a conflict key addresses the category rather than one of its columns.
 *
 * `feeSchedule:categories:{itemId}` is the category — an add or a removal — and
 * `feeSchedule:categories:{itemId}:feeRate` is one of its columns. Both end in a segment
 * {@link proposalFeeScheduleColumnFromConflictKey} cannot place when the column is one this build does
 * not know, so the item's own id is what tells the two apart.
 */
function addressesWholeCategory(conflictKey: string, itemId: string): boolean {
  return lastConflictKeySegment(conflictKey) === itemId;
}

function lastConflictKeySegment(conflictKey: string): string {
  return conflictKey.split(":").at(-1) ?? conflictKey;
}
